import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, ViewStyle } from 'react-native';

import tw from '../styles/tailwind';


type ToggleSize = 'sm' | 'md';

type ToggleProps = {
  value: boolean;
  onValueChange?: (value: boolean) => void;
  disabled?: boolean;
  size?: ToggleSize;
  /** Color de la pista cuando está encendido. Default: primary (turquesa) */
  onColor?: string;
  /** Color de la pista cuando está apagado. Default: gray-200 */
  offColor?: string;
  style?: ViewStyle;
};

const SIZES = {
  sm: { width: 38, height: 22, knob: 16, pad: 3 },
  md: { width: 46, height: 26, knob: 20, pad: 3 },
};

/**
 * Toggle de marca DingDong: pista tipo píldora con perilla animada.
 * Reemplazo directo del <Switch> nativo (misma API value/onValueChange),
 * pero con tamaño/forma consistentes en web y móvil.
 *
 * Anima de inmediato al tocarlo (optimista) y se reconcilia con `value`,
 * para que se sienta responsivo aunque el valor real venga del servidor.
 */
export default function Toggle({
  value,
  onValueChange,
  disabled = false,
  size = 'md',
  onColor,
  offColor,
  style,
}: ToggleProps) {
  const { width, height, knob, pad } = SIZES[size];
  const travel = width - knob - pad * 2;

  const on = (onColor ?? tw.color('primary')) as string;
  const off = (offColor ?? tw.color('gray-200')) as string;

  // Estado interno optimista: refleja el toque al instante y se sincroniza con `value`.
  const [internal, setInternal] = useState(value);
  const anim = useRef(new Animated.Value(value ? 1 : 0)).current;

  useEffect(() => {
    setInternal(value);
  }, [value]);

  useEffect(() => {
    Animated.timing(anim, {
      toValue: internal ? 1 : 0,
      duration: 200,
      easing: Easing.out(Easing.ease),
      useNativeDriver: false, // animamos backgroundColor → no soportado por native driver
    }).start();
  }, [internal]);

  const handlePress = () => {
    const next = !internal;
    setInternal(next);
    onValueChange?.(next);
  };

  const trackColor = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [off, on],
  });

  const translateX = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, travel],
  });

  return (
    <Pressable
      onPress={disabled ? undefined : handlePress}
      disabled={disabled}
      hitSlop={8}
      style={tw.style({ opacity: disabled ? 0.45 : 1 }, style)}
    >
      <Animated.View
        style={{
          width,
          height,
          borderRadius: height / 2,
          backgroundColor: trackColor,
          padding: pad,
          justifyContent: 'center',
        }}
      >
        <Animated.View
          style={{
            width: knob,
            height: knob,
            borderRadius: knob / 2,
            backgroundColor: '#FFFFFF',
            transform: [{ translateX }],
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.12,
            shadowRadius: 1,
            elevation: 1,
          }}
        />
      </Animated.View>
    </Pressable>
  );
}
